import { useState } from "react";
import { useParams } from "react-router-dom";
import { PatientMedicineDto, PatientsMedicineFormData } from "../types/patientMedicine";
import { patientMedicineService } from "../services/patientMedicineService";
import { useApi } from "./useApi";
import { useMutation } from "./useMutation";

export const usePatientMedicines = (overridePatientId?: string) => {
  const { patientId: urlPatientId } = useParams<{ patientId: string }>();
  const patientId = overridePatientId ?? urlPatientId;

  // ΕΝΑ ΚΑΙ ΜΟΝΑΔΙΚΟ request: Επιλέγει αυτόματα endpoint ανάλογα με το αν υπάρχει patientId
  const {
    data: medicines = [],
    loading: isFetching,
    error: getError,
    refetch,
    invalidateCache,
  } = useApi<PatientMedicineDto[]>(
    patientId ? `patient-medicines-${patientId}` : "patient-medicines-all",
    async (signal) => {
      if (patientId) {
        return patientMedicineService.getByPatientId(patientId, signal);
      }
      return patientMedicineService.getAll({}, signal);
    },
    [patientId]
  );

  // Mutation για Create (προσθέτει αυτόματα το patientId από το URL στο payload)
  const {
    execute: createExecution,
    loading: isCreating,
    error: createError,
  } = useMutation((newPatientMedicine: PatientsMedicineFormData) => {
    const payload = patientId 
      ? { ...newPatientMedicine, patientId: Number(patientId) } 
      : newPatientMedicine;
      
    return patientMedicineService.create(payload as any);
  });

  // Mutation για Update (με μοναδικό όνομα execution)
  const {
    execute: updateMedicineExecution,
    loading: isUpdating,
    error: updateError,
  } = useMutation(async ({ id, data }: { id: number; data: PatientsMedicineFormData }) => {
    return patientMedicineService.update(id, data as any);
  });

  // Mutation για Delete
  const {
    execute: deleteExecution,
    loading: isDeleting,
    error: deleteError,
  } = useMutation(async (ids: number[]) => {
    await Promise.all(ids.map((id) => patientMedicineService.delete(id)));
    return ids;
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "success" as "success" | "error",
  });

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  };

  const createPatientMedicine = async (newPatientMedicine: PatientsMedicineFormData): Promise<void> => {
    try {
      await createExecution(newPatientMedicine);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η συνταγογράφηση φαρμάκου αποθηκεύτηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα δημιουργίας";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const updatePatientMedicine = async (id: number, updatedData: PatientsMedicineFormData): Promise<void> => {
    try {
      await updateMedicineExecution({ id, data: updatedData });
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η ενημέρωση της συνταγογράφησης ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα ενημέρωσης φαρμάκου";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  const deletePatientMedicines = async (ids: number[]): Promise<void> => {
    try {
      await deleteExecution(ids);
      invalidateCache();
      setModalState({
        isOpen: true,
        title: "Επιτυχία",
        message: "Η διαγραφή ολοκληρώθηκε επιτυχώς!",
        type: "success",
      });
      refetch(true);
    } catch (err: any) {
      const errorMsg = err.message || "Σφάλμα κατά τη μαζική διαγραφή";
      setModalState({
        isOpen: true,
        title: "Σφάλμα",
        message: errorMsg,
        type: "error",
      });
    }
  };

  return {
    patientMedicines: medicines, 
    allMedicines: medicines,    
    isLoading: isFetching || isCreating || isDeleting || isUpdating,
    error: getError || createError || deleteError || updateError,
    fetchPatientMedicines: () => refetch(true),
    fetchMedicines: () => refetch(true),
    createPatientMedicine,
    updatePatientMedicine,
    deletePatientMedicines,
    modalState,
    closeModal,
  };
};